import { useState } from "react";
import { Layers, Lock } from "lucide-react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface LoginViewProps {
  onLoggedIn: () => void;
}

export function LoginView({ onLoggedIn }: LoginViewProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!password) { setError("Password is required"); return; }

    setSubmitting(true);
    try {
      await api.auth.login(password);
      onLoggedIn();
    } catch (err: any) {
      setError(err.message || "Invalid password");
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-[#f8fafc] px-6">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2.5 mb-10">
          <div className="w-8 h-8 rounded-md bg-brand-600 flex items-center justify-center">
            <Layers className="w-4 h-4 text-white" />
          </div>
          <span className="text-[20px] font-bold text-brand-950 tracking-tight">
            Prism
          </span>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-8">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-slate-400" />
            <h1 className="text-[15px] font-semibold text-brand-950">Sign in</h1>
          </div>
          <p className="text-[13px] text-slate-500 mb-6">
            Enter the shared password to continue
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                disabled={submitting}
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-[13px] text-red-700">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-6">
          See how AI sees your brand.
        </p>
      </div>
    </div>
  );
}
